import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { PageHeader, Alert, LoadingState, Tabs, Tab, Button, Card } from '../ui';
import { ArrowPathIcon, UsersIcon, PlusIcon, StopIcon } from '@heroicons/react/24/outline';
import groupService from '../../services/groupService';

import BasicInfoProcessMonitor from '../BasicInfoProcessMonitor';
import StandardAIAnalysisProcessMonitor from '../StandardAIAnalysisProcessMonitor';
import AccountScannerProcessMonitor from '../AccountScannerProcessMonitor';
import GroupJoinProcessMonitor from '../GroupJoinProcessMonitor';
import UnsubscribeProcessMonitor from '../UnsubscribeProcessMonitor';
import AddGroupsModal from '../AddGroupsModal';

// Import our new modular components
import GroupsFilter from './GroupsFilter';
import GroupsBulkActions from './GroupsBulkActions';
import GroupsMatchedSummary from './GroupsMatchedSummary';
import GroupsTable from './GroupsTable';
import GroupLifecyclePanel from './GroupLifecyclePanel';

// Import our custom hooks
import { useGroupOperations } from '../../hooks/useGroupOperations';
import { useGroupSelection } from '../../hooks/useGroupSelection';

const Groups = () => {
  const [activeTab, setActiveTab] = useState('groups');
  const [showAddGroupsModal, setShowAddGroupsModal] = useState(false);
  const [showGroups, setShowGroups] = useState(false);
  const [showAllGroups, setShowAllGroups] = useState(false);
  const [groupsCount, setGroupsCount] = useState(0);
  const [isLoadingCount, setIsLoadingCount] = useState(false);
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [localSuccess, setLocalSuccess] = useState(null);
  const [localError, setLocalError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 100,
    total: 0
  });
  const [showCleanupConfirm, setShowCleanupConfirm] = useState(false);
  const [isCleaningUp, setIsCleaningUp] = useState(false);

  // Use our custom hooks
  const {
    error,
    success,
    activeBasicInfoProcess,
    activeAIAnalysisProcess,
    activeAccountScannerProcess,
    activeJoinProcess,
    activeUnsubscribeProcess,
    clearError,
    clearSuccess,
    setActiveBasicInfoProcess,
    setActiveAIAnalysisProcess,
    setActiveAccountScannerProcess,
    setActiveJoinProcess,
    setActiveUnsubscribeProcess,
    handleBatchAnalysis,
    handleAccountScan,
    handleGetBasicInfo,
    handleJoinGroups,
    handleBatchUnsubscribe,
    handleUnsubscribe,
    isLoading
  } = useGroupOperations();

  const buildCombinedFilters = (currentFilters = filters, currentSearch = searchTerm) => {
    const combinedFilters = { ...currentFilters };
    if (currentSearch) {
      combinedFilters.search = currentSearch;
    }

    return combinedFilters;
  };

  // Conditional query for groups - only when showGroups is true
  const {
    data: groupsData,
    isLoading: isLoadingGroups,
    error: queryError,
    refetch
  } = useQuery(
    ['groups', filters, searchTerm, pagination.page, pagination.pageSize, showAllGroups],
    () => groupService.getGroups(
      buildCombinedFilters(),
      showAllGroups ? { showAll: true } : pagination
    ),
    {
      enabled: showGroups, // Only fetch when explicitly requested
      retry: 1,
      keepPreviousData: true,
      onSuccess: (data) => {
        if (data.pagination) {
          setPagination(prev => ({
            ...prev,
            total: data.pagination.total
          }));
        }
      },
      onError: (err) => {
        console.error('Error fetching groups:', err);
      }
    }
  );

  const { data: activeProcessesData } = useQuery(
    'activeProcesses',
    () => groupService.getActiveProcesses(),
    {
      refetchInterval: 5000,
      retry: 1,
      onError: (err) => console.error('Error fetching active processes:', err)
    }
  );

  const activeAccountScan = activeProcessesData?.processes?.find(
    (process) => process.process_type === 'ACCOUNT_SCAN' && process.status === 'in_progress'
  );

  useEffect(() => {
    if (activeAccountScan?.id && activeAccountScannerProcess !== activeAccountScan.id) {
      setActiveAccountScannerProcess(activeAccountScan.id);
    }
  }, [activeAccountScan?.id, activeAccountScannerProcess, setActiveAccountScannerProcess]);

  const groups = groupsData?.groups || [];
  const paginationInfo = groupsData?.pagination || { page: 1, limit: pagination.pageSize, total: 0, pages: 0 };

  // Use selection hook with groups data
  const {
    selectedGroups,
    handleSelectGroup,
    handleSelectAll,
    clearSelection,
    selectedCount,
    hasSelection
  } = useGroupSelection(groups);

  // Function to update filters and get count
  const updateFilter = (key, value) => {
    const newFilters = { ...filters };
    if (value === '' || value === null || value === undefined) {
      delete newFilters[key];
    } else {
      newFilters[key] = value;
    }
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
    updateGroupsCount(newFilters, searchTerm);
  };

  // Function to update search and get count
  const updateSearchTerm = (term) => {
    setSearchTerm(term);
    setPagination((prev) => ({ ...prev, page: 1 }));
    updateGroupsCount(filters, term);
  };

  // Function to get groups count by current filters
  const updateGroupsCount = async (currentFilters = filters, currentSearch = searchTerm) => {
    try {
      setIsLoadingCount(true);
      const combinedFilters = buildCombinedFilters(currentFilters, currentSearch);
      const result = await groupService.getGroupsCount(combinedFilters);
      setGroupsCount(result.total);
    } catch (err) {
      console.error('Error getting groups count:', err);
      setGroupsCount(0);
    } finally {
      setIsLoadingCount(false);
    }
  };

  // Function to clear filters
  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setShowGroups(false);
    setShowAllGroups(false);
    setPagination({
      page: 1,
      pageSize: 100,
      total: 0
    });
    updateGroupsCount({}, '');
  };

  // Check if has active filters
  const hasActiveFilters = Object.keys(filters).length > 0 || searchTerm.length > 0;
  const canRunFilterBulkOperation = hasActiveFilters && groupsCount > 0;

  // Initial count on mount
  useEffect(() => {
    let isMounted = true;

    const loadInitialCount = async () => {
      try {
        setIsLoadingCount(true);
        const result = await groupService.getGroupsCount({});
        if (isMounted) {
          setGroupsCount(result.total);
        }
      } catch (err) {
        console.error('Error getting initial groups count:', err);
        if (isMounted) {
          setGroupsCount(0);
        }
      } finally {
        if (isMounted) {
          setIsLoadingCount(false);
        }
      }
    };

    loadInitialCount();

    return () => {
      isMounted = false;
    };
  }, []);

  const { data: filterOptions } = useQuery(
    'groupFilterOptions',
    () => groupService.getFilterOptions(),
    {
      retry: 1,
      staleTime: 60000,
      onError: (err) => console.error('Error fetching group filter options:', err)
    }
  );

  // Handle operations with our hooks
  const handleBulkAnalysis = async (settings) => {
    const result = await handleBatchAnalysis(selectedGroups, settings);
    if (result?.success) {
      clearSelection();
    }
  };

  const handleBasicInfo = async (settings) => {
    const result = await handleGetBasicInfo(selectedGroups, settings);
    if (result?.success) {
      clearSelection();
    }
  };

  // Handle group joining with settings
  const handleJoinGroupsWithSettings = async (settings) => {
    const result = await handleJoinGroups(selectedGroups, settings);
    if (result?.success) {
      clearSelection();
    }
  };

  // Handle batch unsubscribe with settings
  const handleBatchUnsubscribeWithSettings = async (settings) => {
    const result = await handleBatchUnsubscribe(selectedGroups, settings);
    if (result?.success) {
      clearSelection();
    }
  };

  // Handle cleanup all processes
  const handleCleanupAllProcesses = async () => {
    try {
      setIsCleaningUp(true);
      setLocalError(null);
      
      const result = await groupService.cleanupAllProcesses();
      
      if (result?.cancelled >= 0) {
        setLocalSuccess(`✅ Cleanup завершен: оставлен 1 процесс, отключено ${result.cancelled} дублирующихся процессов`);
        setShowCleanupConfirm(false);
        
        // Close any active process monitors for cancelled processes
        if (result.processes) {
          result.processes.forEach(proc => {
            if (proc.action === 'cancelled' && activeBasicInfoProcess === proc.processId) {
              setActiveBasicInfoProcess(null);
            }
          });
        }
      } else {
        setLocalError('Не удалось выполнить cleanup процессов');
      }
    } catch (err) {
      console.error('Error cleaning up processes:', err);
      setLocalError(`Ошибка cleanup: ${err.message}`);
    } finally {
      setIsCleaningUp(false);
    }
  };

  const handleExportFilteredGroups = async () => {
    if (!canRunFilterBulkOperation) {
      setLocalSuccess(null);
      setLocalError('Add at least one filter with matching groups before export.');
      return;
    }

    try {
      setIsExporting(true);
      setLocalError(null);

      const response = await groupService.exportGroups(buildCombinedFilters());
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'text/csv;charset=utf-8',
      });

      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
      const filename = filenameMatch?.[1] || `groups-filtered-${new Date().toISOString().slice(0, 10)}.csv`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setLocalSuccess(`Export completed for ${groupsCount.toLocaleString()} matched groups`);
    } catch (err) {
      console.error('Error exporting filtered groups:', err);
      setLocalSuccess(null);
      setLocalError(`Failed to export groups: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Handle bulk operations by filters (not by selection)
  const handleFilterBulkOperation = async (operationType) => {
    try {
      if (!canRunFilterBulkOperation) {
        setLocalSuccess(null);
        setLocalError('Выберите фильтр или поиск перед массовой операцией. Без фильтра операция может затронуть все группы.');
        return;
      }

      const operationNames = {
        basicInfo: 'Basic Info collection',
        analysis: 'AI Analysis',
        join: 'Group joining',
        unsubscribe: 'Batch unsubscribe'
      };

      const confirmed = window.confirm(
        `${operationNames[operationType] || operationType} will run for ${groupsCount.toLocaleString()} filtered groups. Continue?`
      );
      if (!confirmed) {
        return;
      }

      const combinedFilters = { ...filters };
      if (searchTerm) combinedFilters.search = searchTerm;

      let result;
      switch (operationType) {
        case 'basicInfo':
          result = await groupService.batchGetBasicInfo({
            filters: combinedFilters,
            maxGroups: groupsCount, // 🚀 UNLIMITED - Process all groups
            maxConcurrent: 5, // 🚀 INCREASED concurrency
            delayBetween: 2000 // 🚀 OPTIMIZED delay
          });
          if (result?.processId) {
            setActiveBasicInfoProcess(result.processId);
          }
          break;

        case 'analysis':
          result = await groupService.batchAnalyze({
            filters: combinedFilters,
            maxGroups: groupsCount, // Process all groups
            force: false
          });
          if (result?.processId) {
            setActiveAIAnalysisProcess(result.processId);
          }
          break;

        case 'join':
          result = await groupService.batchJoinGroups({
            filters: combinedFilters,
            maxGroups: groupsCount, // Process all groups
            maxConcurrent: 1,
            delayBetween: 3000,
            maxRetries: 3
          });
          if (result?.processId) {
            setActiveJoinProcess(result.processId);
          }
          break;

        case 'unsubscribe':
          result = await groupService.batchUnsubscribe({
            filters: combinedFilters,
            maxGroups: groupsCount, // Process all groups
            delayBetween: 1000
          });
          if (result?.processId) {
            setActiveUnsubscribeProcess(result.processId);
          }
          break;


        default:
          throw new Error(`Unknown operation type: ${operationType}`);
      }

      if (result?.success !== false) {
        setLocalError(null);
        setLocalSuccess(`${operationNames[operationType]} started for ${result?.queued || groupsCount} groups`);
      }
    } catch (err) {
      console.error(`Error in ${operationType} operation:`, err);
      setLocalSuccess(null);
      setLocalError(`Failed to start ${operationType} operation: ${err.message}`);
    }
  };

  if (isLoadingGroups) {
    return <LoadingState message="Loading groups..." />;
  }

  return (
    <div>
      <PageHeader
        title="Groups Management"
        description="Manage Telegram groups with AI categorization and bulk operations"
        actions={
          <div className="flex flex-wrap gap-2">
            <button
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={() => refetch()}
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Refresh
            </button>
            <button
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              onClick={() => setShowAddGroupsModal(true)}
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Groups
            </button>
            <button
              className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                activeAccountScan || isLoading.accountScan
                  ? 'bg-indigo-400 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
              onClick={handleAccountScan}
              disabled={Boolean(activeAccountScan) || isLoading.accountScan}
            >
              <UsersIcon className="h-4 w-4 mr-2" />
              {activeAccountScan ? 'Scan Running' : isLoading.accountScan ? 'Scanning...' : 'Scan Accounts'}
            </button>
            <button
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              onClick={() => setShowCleanupConfirm(true)}
              disabled={isCleaningUp}
            >
              <StopIcon className="h-4 w-4 mr-2" />
              {isCleaningUp ? 'Очистка...' : '🚫 Отключить все процессы'}
            </button>
          </div>
        }
      />

      {queryError && (
        <Alert variant="error" className="mb-4">
          {queryError.message || 'Failed to load groups'}
        </Alert>
      )}

      {(error || localError) && (
        <Alert variant="error" className="mb-4" onClose={() => { clearError(); setLocalError(null); }}>
          {error || localError}
        </Alert>
      )}

      {(success || localSuccess) && (
        <Alert variant="success" className="mb-4" onClose={() => { clearSuccess(); setLocalSuccess(null); }}>
          {success || localSuccess}
        </Alert>
      )}

      {/* Process Monitors */}
      {activeBasicInfoProcess && (
        <BasicInfoProcessMonitor
          processId={activeBasicInfoProcess}
          onClose={() => setActiveBasicInfoProcess(null)}
        />
      )}

      {activeAIAnalysisProcess && (
        <StandardAIAnalysisProcessMonitor
          processId={activeAIAnalysisProcess}
          onClose={() => setActiveAIAnalysisProcess(null)}
        />
      )}

      {activeAccountScannerProcess && (
        <AccountScannerProcessMonitor
          processId={activeAccountScannerProcess}
          onClose={() => setActiveAccountScannerProcess(null)}
        />
      )}

      {activeJoinProcess && (
        <GroupJoinProcessMonitor
          processId={activeJoinProcess}
          onClose={() => setActiveJoinProcess(null)}
        />
      )}

      {activeUnsubscribeProcess && (
        <UnsubscribeProcessMonitor
          processId={activeUnsubscribeProcess}
          onClose={() => setActiveUnsubscribeProcess(null)}
        />
      )}


      <Tabs activeTab={activeTab} onChange={setActiveTab}>
        <Tab id="groups" label="Groups Management">
          <div className="space-y-4">
            <GroupsFilter
              searchTerm={searchTerm}
              setSearchTerm={updateSearchTerm}
              filters={filters}
              updateFilter={updateFilter}
              countries={filterOptions?.countries || []}
              languages={filterOptions?.languages || []}
              regions={filterOptions?.regions || []}
              clearFilters={clearFilters}
              hasActiveFilters={hasActiveFilters}
            />

            <GroupsMatchedSummary
              groupsCount={groupsCount}
              hasActiveFilters={hasActiveFilters}
              isLoadingCount={isLoadingCount}
              showGroups={showGroups}
              onShowGroupsToggle={() => setShowGroups((previous) => !previous)}
              onExport={handleExportFilteredGroups}
              isExporting={isExporting}
              canExport={canRunFilterBulkOperation}
            />

            <Card>
              <div className="p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  Actions for matched groups
                </h4>
                {!hasActiveFilters && (
                  <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    Choose a filter or search term before running bulk actions. Without a filter this can target the full groups base.
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleFilterBulkOperation('basicInfo')}
                    disabled={isLoading.basicInfo || !canRunFilterBulkOperation}
                  >
                    {isLoading.basicInfo ? 'Получение инфо...' : 'Get Basic Info'}
                  </Button>

                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleFilterBulkOperation('analysis')}
                    disabled={isLoading.batchAnalysis || !canRunFilterBulkOperation}
                  >
                    {isLoading.batchAnalysis ? 'Анализ...' : 'AI Analysis'}
                  </Button>

                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => handleFilterBulkOperation('join')}
                    disabled={isLoading.joinGroups || !canRunFilterBulkOperation}
                  >
                    {isLoading.joinGroups ? 'Вступление...' : 'Join Groups'}
                  </Button>

                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleFilterBulkOperation('unsubscribe')}
                    disabled={isLoading.batchUnsubscribe || !canRunFilterBulkOperation}
                  >
                    {isLoading.batchUnsubscribe ? 'Отписка...' : 'Unsubscribe'}
                  </Button>
                </div>

                <p className="text-xs text-gray-500 mt-2">
                  Bulk actions apply to every group that matches the current filters, not only the visible rows.
                </p>
              </div>
            </Card>

            {/* Groups Table - only show when requested */}
            {showGroups && (
              <>
                {/* Show All Toggle */}
                <Card>
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <h4 className="text-lg font-medium text-gray-900">Список групп</h4>
                        <span className="text-sm text-gray-500">
                          Показано: {groups.length.toLocaleString()} из {paginationInfo.total.toLocaleString()}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={showAllGroups}
                            onChange={(e) => setShowAllGroups(e.target.checked)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <span className="text-sm font-medium text-gray-700">
                            Показать все группы
                          </span>
                        </label>
                        {showAllGroups && (
                          <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                            Внимание: загружены все группы
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Selection Actions for displayed groups */}
                {hasSelection && (
                <GroupsBulkActions
                  selectedCount={selectedCount}
                  clearSelection={clearSelection}
                  onGetBasicInfo={handleBasicInfo}
                  onBatchAnalysis={handleBulkAnalysis}
                  onJoinGroups={handleJoinGroupsWithSettings}
                  onBatchUnsubscribe={handleBatchUnsubscribeWithSettings}
                  isLoading={{
                    basicInfo: false,
                    batchAnalysis: isLoading.batchAnalysis,
                    joinGroups: false,
                    batchUnsubscribe: isLoading.batchUnsubscribe
                  }}
                />
                )}

                {/* Groups Table */}
                <GroupsTable
                  groups={groups}
                  selectedGroups={selectedGroups}
                  onSelectGroup={handleSelectGroup}
                  onSelectAll={handleSelectAll}
                  onUnsubscribe={handleUnsubscribe}
                  onAccountScan={handleAccountScan}
                  isLoading={isLoading}
                  paginationInfo={paginationInfo}
                  searchTerm={searchTerm}
                  hasActiveFilters={hasActiveFilters}
                />
              </>
            )}
          </div>
        </Tab>


        <Tab id="lifecycle" label="Автообработка">
          <GroupLifecyclePanel selectedChatIds={Array.from(selectedGroups)} />
        </Tab>
      </Tabs>

      {/* Add Groups Modal */}
      <AddGroupsModal
        isOpen={showAddGroupsModal}
        onClose={() => setShowAddGroupsModal(false)}
        onProcessStarted={(processId) => {
          // Handle process started if needed
          console.log('Add groups process started:', processId);
          refetch(); // Refresh groups list
        }}
      />

      {/* Cleanup Confirmation Modal */}
      {showCleanupConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative p-8 bg-white w-full max-w-md m-auto rounded-lg shadow-lg">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <StopIcon className="h-6 w-6 text-red-600" />
              </div>
              
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">
                Отключить все процессы Basic Info?
              </h3>
              
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 mb-4">
                  Это действие найдет все активные процессы Basic Info и оставит только самый последний, 
                  отключив все дублирующиеся процессы.
                </p>
                
                <div className="bg-amber-50 border-l-4 border-amber-400 p-3 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-amber-700">
                        ⚠️ <strong>Внимание:</strong> Все процессы кроме самого последнего будут отменены
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-3 justify-center">
                <button
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={() => setShowCleanupConfirm(false)}
                  disabled={isCleaningUp}
                >
                  Отмена
                </button>
                
                <button
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  onClick={handleCleanupAllProcesses}
                  disabled={isCleaningUp}
                >
                  {isCleaningUp ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Очистка...
                    </>
                  ) : (
                    '🚫 Отключить все процессы'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Groups;
